import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper,
  Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography,
} from "@material-ui/core";
import { useNotify } from "react-admin";
import { cloError, cloGet, cloPost, ENDPOINTS } from "./api";
import MerchantPicker from "./MerchantPicker";
import PatternDialog from "./PatternDialog";

const UnmatchedTab: React.FC = () => {
  const notify = useNotify();
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("open");
  const [resolving, setResolving] = useState<any | null>(null);
  const [cloMerchantId, setCloMerchantId] = useState("");
  const [addingPattern, setAddingPattern] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await cloGet(ENDPOINTS.CLO_UNMATCHED_DESCRIPTORS, { page: page + 1, pageSize, search: search || undefined, status: status || undefined });
      setRows(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch (e) {
      notify(cloError(e), "warning");
    }
  }, [page, pageSize, search, status, notify]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const act = async (body: object, message: string) => {
    try {
      await cloPost(ENDPOINTS.CLO_UNMATCHED_DESCRIPTORS, body);
      notify(message, "info");
      setResolving(null);
      load();
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  const openResolve = (row: any) => {
    setResolving(row);
    setCloMerchantId("");
  };

  const resolve = () => act({ action: "resolve", descriptorId: resolving.id, cloMerchantId: Number(cloMerchantId) }, "Resolved");

  return (
    <Paper>
      <Box p={2} display="flex" alignItems="center" style={{ gap: 16 }}>
        <TextField label="Search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
        <TextField select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }} style={{ minWidth: 140 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="open">open</MenuItem>
          <MenuItem value="resolved">resolved</MenuItem>
          <MenuItem value="ignored">ignored</MenuItem>
        </TextField>
      </Box>
      <Box px={2}>
        <Typography variant="body2" color="textSecondary">
          Bank text that looks like a known shop but matched no pattern. Resolve it by adding a pattern to the right merchant, or ignore it.
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Bank text</TableCell>
            <TableCell>Bank merchant name</TableCell>
            <TableCell>Transactions</TableCell>
            <TableCell>First / last seen</TableCell>
            <TableCell>Status</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell><code>{r.descriptor}</code></TableCell>
              <TableCell>{r.bank_merchant_name || "—"}</TableCell>
              <TableCell>{r.txn_count}</TableCell>
              <TableCell>
                <Typography variant="caption" display="block">{r.first_seen_at || "—"}</Typography>
                <Typography variant="caption" display="block">{r.last_seen_at || "—"}</Typography>
              </TableCell>
              <TableCell>{r.status}{r.merchant_name ? ` · ${r.merchant_name}` : ""}</TableCell>
              <TableCell align="right">
                {r.status === "open" ? (
                  <>
                    <Button size="small" color="primary" onClick={() => openResolve(r)}>Resolve</Button>
                    <Button size="small" onClick={() => act({ action: "ignore", descriptorId: r.id }, "Ignored")}>Ignore</Button>
                  </>
                ) : (
                  <Button size="small" onClick={() => act({ action: "reopen", descriptorId: r.id }, "Reopened")}>Reopen</Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={6} align="center">Nothing in the queue</TableCell></TableRow>}
        </TableBody>
      </Table>
      <TablePagination
        component="div" count={total} page={page} rowsPerPage={pageSize}
        onChangePage={(_, p) => setPage(p)}
        onChangeRowsPerPage={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
      />

      <Dialog disableBackdropClick open={resolving !== null} onClose={() => setResolving(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve <code>{resolving?.descriptor}</code></DialogTitle>
        <DialogContent>
          <MerchantPicker label="Merchant" value={cloMerchantId} onChange={setCloMerchantId} />
          <Typography variant="body2" color="textSecondary">
            Add a pattern so this text matches from now on. You'll preview it before it's saved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolving(null)}>Cancel</Button>
          <Button disabled={!cloMerchantId} onClick={resolve}>Resolve without pattern</Button>
          <Button color="primary" variant="contained" disabled={!cloMerchantId} onClick={() => setAddingPattern(true)}>Add pattern</Button>
        </DialogActions>
      </Dialog>

      {resolving && cloMerchantId && (
        <PatternDialog
          open={addingPattern}
          cloMerchantId={Number(cloMerchantId)}
          pattern={null}
          initialText={resolving.descriptor}
          onClose={() => setAddingPattern(false)}
          onSaved={() => { setAddingPattern(false); resolve(); }}
        />
      )}
    </Paper>
  );
};

export default UnmatchedTab;
